import { css } from '@emotion/react';

export default $ => css`
  /* ── Root: fill available space, stack sections vertically ──────────────── */
  .cardtray {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  /* ── Card shell layout (ui:asCard === true) ──────────────────────────────── */
  .cardtray--as-card {
    > .card:not(:first-of-type) {
      flex-grow: 1;
      min-height: 0;
    }
    > .card {
      position: relative;
    }
    .cardtray-card-body {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      padding: 1rem;
      overflow: hidden;
    }
  }

  /* ── Header row (flat layout) ────────────────────────────────────────────── */
  .cardtray-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    flex-shrink: 0;

    .cardtray-header-left {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .cardtray-header-title {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;

      span,
      h4 {
        margin: 0;
        font-weight: 500;
        line-height: 1.3;
      }

      small,
      .worktray-description small {
        font-size: 0.8125rem;
      }
    }

    .filter-button {
      flex-shrink: 0;
      border: 0 !important;
      transition: transofrm 250ms ease-in-out;
      box-shadow: none;
      padding-left: 4px;
      padding-right: 4px;
      margin-right: 10px;
      box-shadow: none !important;

      &:hover,
      &:active,
      &.active {
        box-shadow: none !important;
        background: transparent !important;
        color: ${$.gray400} !important;
        transform: scale(1.3) !important;
      }
    }
  }

  /* ── Filter collapse ─────────────────────────────────────────────────────── */
  .worktray-filters-collapse {
    // margin-bottom: 1rem;
    flex-shrink: 0;
    top: 0;
  }

  /* ── Loader / notifications ──────────────────────────────────────────────── */
  .cardtray-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
    min-height: 0;
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    right: 0;
  }

  .cardtray-notification {
    margin-bottom: 1rem;
    flex-shrink: 0;
  }

  /* ── Pagination (flat layout) ────────────────────────────────────────────── */
  .cardtray-pagination {
    margin-top: 1rem;
    flex-shrink: 0;
  }

  /* ── Scrollable grid wrapper ─────────────────────────────────────────────── */
  .cardtray-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-right: 0.25rem;
    position: relative;
  }

  /* ── Card grid ───────────────────────────────────────────────────────────────
   *
   * .cardtray-grid--cols-{n} sets the MAXIMUM columns at the widest breakpoint.
   * Narrower breakpoints always win, so the grid collapses gracefully on mobile.
   *
   * Breakpoint ladder (mirrors Bootstrap's sm / md / lg):
   *   xs  (< 576px) → always 1 column
   *   sm  (≥ 576px) → up to 2 columns  (unless max is 1)
   *   md  (≥ 768px) → up to 3 columns  (unless max is 1 or 2)
   *   lg  (≥ 992px) → up to 4 columns  (cols-4 only)
   */
  .cardtray-grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: 1fr;
    padding-bottom: 0.5rem;
    padding-top: 4px;

    &.cardtray-grid--cols-1 {
      grid-template-columns: 1fr;
    }

    &.cardtray-grid--cols-2 {
      @media (min-width: 576px) {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    &.cardtray-grid--cols-3 {
      @media (min-width: 576px) {
        grid-template-columns: repeat(2, 1fr);
      }
      @media (min-width: 768px) {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    &.cardtray-grid--cols-4 {
      @media (min-width: 576px) {
        grid-template-columns: repeat(2, 1fr);
      }
      @media (min-width: 768px) {
        grid-template-columns: repeat(3, 1fr);
      }
      @media (min-width: 992px) {
        grid-template-columns: repeat(4, 1fr);
      }
    }
  }

  /* ── Individual item card ────────────────────────────────────────────────── */
  .cardtray-item {
    display: flex;
    flex-direction: column;
    height: 100%;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 0.5rem;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.2s ease, transform 0.15s ease;

    &:hover {
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
      border-color: ${$ ? $.primaryMain : 'var(--primary)'};
      z-index: 10;
    }

    > .card-body {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 1rem 1.25rem;
      height: 100%;
    }

    .cardtray-item-content {
      flex: 1;
      line-height: 1.4;

      h3 {
        margin-top: 0;
        margin-bottom: 0.1rem;
        font-size: 1rem;
        font-weight: 700;
        // color: #1a1a1a;
      }

      h6 {
        margin-top: 0;
        margin-bottom: 0.5rem;
        font-size: 0.7rem;
        font-weight: 600;
        color: #888;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }

      h1,
      h2,
      h4,
      h5 {
        margin-top: 0;
        margin-bottom: 0.35rem;
        font-size: 1rem;
        font-weight: 600;
      }

      p {
        margin-bottom: 0.25rem;
        font-size: 0.875rem;
        // color: #444;

        &:last-child {
          margin-bottom: 0;
        }
      }

      ul,
      ol {
        padding-left: 1.2rem;
        margin-bottom: 0.4rem;
        font-size: 0.875rem;
      }

      strong {
        font-weight: 600;
      }

      em {
        font-size: 0.75rem;
        color: #aaa;
      }
    }

    .cardtray-item-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      padding-top: 0.5rem;
      justify-content: flex-end;

      .btn {
        font-size: 0.8125rem;
        padding: 0.3rem 0.75rem;
      }
    }
  }
`;
